import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import LoginIcon from '@mui/icons-material/Login';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import LogoutIcon from '@mui/icons-material/Logout';

import PageHeader from '../core/PageHeader';
import { useAlert } from '../core/AlertContext';
import { customFetcher } from '../hooks/customFetcher';
import {
    Meeting,
    useCreateMeeting,
    useEndMeeting,
    useJitsiConfig,
    useMeetings,
    useMeetingToken,
} from '../hooks/meetings';
import { getCurrentUserIdFromStorage, isSuperUser } from '../blog/blogPermissions';
import JitsiRoom from './JitsiRoom';
import { primaryButtonSx, secondaryButtonSx } from '../core/pvtaStyles';

const MANAGER_GROUP_IDS = [1, 2, 3];

const getStoredGroups = (): number[] => {
    try {
        const parsed = JSON.parse(localStorage.getItem('groups') ?? '[]') as unknown;
        return Array.isArray(parsed) ? parsed.filter((item): item is number => typeof item === 'number') : [];
    } catch {
        return [];
    }
};

const isManager = (): boolean =>
    isSuperUser() || getStoredGroups().some((id) => MANAGER_GROUP_IDS.includes(id));

const canEndMeeting = (meeting: Meeting): boolean =>
    isManager() || meeting.created_by?.id === getCurrentUserIdFromStorage();

interface CurrentUserProfile {
    first_name?: string;
    last_name?: string;
    email?: string;
}

const MeetingPage: React.FC = () => {
    const { showAlert } = useAlert();
    const { data: meetings, isLoading } = useMeetings();
    const { data: config } = useJitsiConfig();
    const createMeeting = useCreateMeeting();
    const endMeeting = useEndMeeting();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [meetingName, setMeetingName] = useState('');
    const [activeMeeting, setActiveMeeting] = useState<Meeting | null>(null);

    const { data: profile } = useQuery<CurrentUserProfile, Error>({
        queryKey: ['accounts', 'me'],
        staleTime: 5 * 60 * 1000,
        queryFn: async () => customFetcher<CurrentUserProfile>({ endpoint: '/accounts/me/' }),
    });

    const { data: tokenData, isLoading: tokenLoading } = useMeetingToken(activeMeeting?.id ?? null);

    const displayName =
        [profile?.first_name, profile?.last_name].filter(Boolean).join(' ').trim() ||
        profile?.email ||
        undefined;

    const handleCreate = async () => {
        const name = meetingName.trim();
        if (!name) {
            return;
        }
        try {
            const meeting = await createMeeting.mutateAsync({ name });
            setDialogOpen(false);
            setMeetingName('');
            setActiveMeeting(meeting);
        } catch {
            showAlert({ severity: 'error', text: 'Meeting konnte nicht erstellt werden.' });
        }
    };

    const handleEnd = async (meeting: Meeting) => {
        try {
            await endMeeting.mutateAsync({ id: meeting.id });
            if (activeMeeting?.id === meeting.id) {
                setActiveMeeting(null);
            }
            showAlert({ severity: 'success', text: 'Meeting beendet.' });
        } catch {
            showAlert({ severity: 'error', text: 'Meeting konnte nicht beendet werden.' });
        }
    };

    // Aktive Konferenz-Ansicht
    if (activeMeeting && config?.domain) {
        return (
            <Box sx={{ py: { xs: 2, md: 3 }, width: '100%', maxWidth: '100%' }}>
                <Container maxWidth="xl">
                    <PageHeader
                        title={activeMeeting.name}
                        action={(
                            <Button
                                variant="outlined"
                                color="inherit"
                                startIcon={<LogoutIcon />}
                                onClick={() => setActiveMeeting(null)}
                            >
                                Verlassen
                            </Button>
                        )}
                    />
                    {tokenLoading ? (
                        <Typography color="text.secondary">Konferenz wird vorbereitet …</Typography>
                    ) : (
                        <JitsiRoom
                            domain={config.domain}
                            roomName={activeMeeting.room_name}
                            displayName={displayName}
                            subject={activeMeeting.name}
                            jwt={tokenData?.jwt}
                            onClose={() => setActiveMeeting(null)}
                        />
                    )}
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={{ py: { xs: 3, md: 4 }, width: '100%', maxWidth: '100%' }}>
            <Container maxWidth="md">
                <PageHeader
                    title="Meeting"
                    subtitle="Starte eine Online-Konferenz oder tritt einer laufenden bei."
                    action={(
                        <Button
                            sx={{ ...primaryButtonSx }}
                            startIcon={<VideoCallIcon />}
                            onClick={() => setDialogOpen(true)}
                        >
                            Meeting starten
                        </Button>
                    )}
                />

                {isLoading ? (
                    <Typography color="text.secondary">Lädt …</Typography>
                ) : meetings.length === 0 ? (
                    <Typography color="text.secondary">
                        Aktuell läuft kein Meeting. Starte eines über „Meeting starten".
                    </Typography>
                ) : (
                    <Stack spacing={2}>
                        {meetings.map((meeting) => (
                            <Card key={meeting.id} variant="outlined">
                                <CardContent>
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={2}
                                        alignItems={{ xs: 'stretch', sm: 'center' }}
                                        justifyContent="space-between"
                                    >
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="h6" noWrap>
                                                {meeting.name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" noWrap>
                                                Erstellt von {meeting.created_by?.displayName ?? 'Unbekannt'}
                                            </Typography>
                                        </Box>
                                        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                                            <Button
                                                sx={{ ...primaryButtonSx }}
                                                startIcon={<LoginIcon />}
                                                onClick={() => setActiveMeeting(meeting)}
                                            >
                                                Beitreten
                                            </Button>
                                            {canEndMeeting(meeting) && (
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    startIcon={<StopCircleIcon />}
                                                    disabled={endMeeting.isPending}
                                                    onClick={() => handleEnd(meeting)}
                                                >
                                                    Beenden
                                                </Button>
                                            )}
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                )}
            </Container>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Neues Meeting</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Name des Meetings"
                        fullWidth
                        value={meetingName}
                        onChange={(e) => setMeetingName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                void handleCreate();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button sx={secondaryButtonSx} onClick={() => setDialogOpen(false)}>Abbrechen</Button>
                    <Button
                        sx={{ ...primaryButtonSx }}
                        onClick={() => void handleCreate()}
                        disabled={!meetingName.trim() || createMeeting.isPending}
                    >
                        Starten
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MeetingPage;
